/**
 * Legal-content update channel — separate from the app's own release cycle.
 *
 * The company publishes a versioned "content update" (the curated CASELAW
 * list + the rebuilt caselaw.sqlite, packaged by scripts/publish-content-
 * update.ts) to a small, dedicated static host, independent of shipping a
 * new app build. Attorneys check for it explicitly (or it's checked
 * automatically while online) and choose to download and apply it, or
 * decline and keep working with whatever is already on the device — an
 * update is never applied without that explicit choice.
 *
 * Two pieces of downloaded data are stored on-device once accepted:
 *   - caselaw.json (curated statutes/rules/cases) → localStorage (small),
 *     applied by mutating the CASELAW array in place at startup so every
 *     existing import site (law.ts, reports.ts, etc.) sees it automatically.
 *   - caselaw.sqlite (full opinions) → Cache API (too large for
 *     localStorage), consumed by data/caselaw-db.ts's fetchDbBytes(), which
 *     prefers this override over the app's bundled copy when present.
 */
import { CASELAW } from '@/data/caselaw';
import { byId } from '@/ui/dom';
import { showToast } from '@/ui/overlays';

/**
 * The content-version baked into THIS build's compiled-in src/data/caselaw.ts.
 * Bump whenever caselaw.ts ships new/corrected data in an app release, so a
 * fresh install correctly reports what it already has without needing a
 * network check first.
 */
const BASELINE_VERSION = '2026-07-24';

const MANIFEST_URL = 'https://spatchai-legal-updates.pages.dev/manifest.json';
const UPDATE_ORIGIN = 'https://spatchai-legal-updates.pages.dev/';

const APPLIED_KEY = 'spatchai_content_update_applied'; // { version, appliedAt, caselaw }
const CACHE_NAME = 'spatchai-content-update';
const CACHE_DB_KEY = '/__content-update__/caselaw.sqlite';

interface Manifest {
  version: string;
  publishedAt: string;
  summary: string;
  changelog: string[];
  caselawUrl: string;
  caselawCount: number;
  sqliteUrl: string;
  sqliteSchemaVersion: number;
  sqliteBytes: number;
  /** Peer-reviewed / practitioner jury-selection research findings bundled in the sqlite. */
  juryResearchCount?: number;
}

interface AppliedRecord {
  version: string;
  appliedAt: string;
  caselaw: unknown[];
}

function loadApplied(): AppliedRecord | null {
  try {
    const raw = localStorage.getItem(APPLIED_KEY);
    return raw ? (JSON.parse(raw) as AppliedRecord) : null;
  } catch {
    return null;
  }
}

/** The version this device is actually running with right now. */
export function getCurrentVersion(): string {
  return loadApplied()?.version || BASELINE_VERSION;
}

/**
 * Apply any previously-downloaded-and-accepted curated-authorities override.
 * Call once at startup, before any page renders — synchronous and offline
 * (reads localStorage only), so it works with zero network every load.
 */
export function applyStoredContentUpdate(): void {
  const applied = loadApplied();
  if (!applied || !Array.isArray(applied.caselaw) || !applied.caselaw.length) return;
  CASELAW.length = 0;
  CASELAW.push(...(applied.caselaw as typeof CASELAW));
}

let latestManifest: Manifest | null = null;
let checking = false;
let downloading = false;
let dismissedVersion: string | null = null; // "Not Now" for this session only

/**
 * Fetch the manifest and compare against what's currently applied. Online
 * only. `resetDismissed` clears a prior "Not Now" so a deliberate, explicit
 * re-check (the button) always reflects current status — background checks
 * (on load / on reconnect) leave a decline in place so they don't re-nag.
 */
export async function checkForContentUpdate(resetDismissed = false): Promise<void> {
  if (checking || !navigator.onLine) return;
  if (resetDismissed) dismissedVersion = null;
  checking = true;
  renderContentUpdateStatus();
  try {
    const resp = await fetch(MANIFEST_URL, { cache: 'no-store' });
    if (!resp.ok) throw new Error(`manifest fetch: HTTP ${resp.status}`);
    const manifest = (await resp.json()) as Manifest;
    latestManifest = manifest.version !== getCurrentVersion() ? manifest : null;
  } catch {
    /* offline or host unreachable — silently keep working with what's on-device */
  } finally {
    checking = false;
    renderContentUpdateStatus();
  }
}

/** Download the available update and apply it on-device. */
export async function downloadContentUpdate(): Promise<void> {
  if (!latestManifest || downloading) return;
  const manifest = latestManifest;
  downloading = true;
  renderContentUpdateStatus();
  showToast(`Downloading legal-content update ${manifest.version}…`, 2500);
  try {
    const [caselawResp, sqliteResp] = await Promise.all([
      fetch(new URL(manifest.caselawUrl, UPDATE_ORIGIN).toString(), { cache: 'no-store' }),
      fetch(new URL(manifest.sqliteUrl, UPDATE_ORIGIN).toString(), { cache: 'no-store' })
    ]);
    if (!caselawResp.ok) throw new Error(`caselaw fetch: HTTP ${caselawResp.status}`);
    if (!sqliteResp.ok) throw new Error(`sqlite fetch: HTTP ${sqliteResp.status}`);
    const caselawJson = (await caselawResp.json()) as unknown[];

    // Store the DB override in the Cache API (large binary; localStorage can't hold it).
    const cache = await caches.open(CACHE_NAME);
    await cache.put(CACHE_DB_KEY, sqliteResp.clone());

    // Store + apply the curated-authorities override.
    const record: AppliedRecord = { version: manifest.version, appliedAt: new Date().toISOString(), caselaw: caselawJson };
    localStorage.setItem(APPLIED_KEY, JSON.stringify(record));
    CASELAW.length = 0;
    CASELAW.push(...(caselawJson as typeof CASELAW));

    // Force the SQLite runtime to re-import from the new override on next open.
    const { removeCaselawDbInstance } = await import('@/data/caselaw-db-instance');
    await removeCaselawDbInstance();

    latestManifest = null;
    showToast(`Legal content updated to ${manifest.version}.`, 4000);
  } catch (err) {
    console.error('downloadContentUpdate failed:', err);
    showToast('Update failed to download — still working with your existing legal content.', 4000);
  } finally {
    downloading = false;
    renderContentUpdateStatus();
  }
}

/** Keep working with what's already on-device; don't apply the available update. */
export function declineContentUpdate(): void {
  if (latestManifest) dismissedVersion = latestManifest.version;
  renderContentUpdateStatus();
}

/**
 * The stored SQLite override bytes, if an update has been downloaded and
 * applied — consumed by data/caselaw-db.ts's fetchDbBytes() in preference to
 * the app's bundled copy. Null if no override is stored (normal case).
 */
export async function getContentUpdateDbOverrideBytes(): Promise<Uint8Array | null> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const resp = await cache.match(CACHE_DB_KEY);
    if (!resp) return null;
    return new Uint8Array(await resp.arrayBuffer());
  } catch {
    return null;
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Nudge thresholds for the staleness reminder — content updates are published
// roughly weekly, so these give the attorney a couple of quiet cycles before
// escalating to something he can't miss on the Case tab.
const STALE_WARN_DAYS = 14;
const STALE_URGENT_DAYS = 30;

/** When the content currently on this device was last updated. */
function effectiveContentDate(): Date {
  const applied = loadApplied();
  // BASELINE_VERSION is a 'YYYY-MM-DD' string, so it doubles as its own effective date
  // when nothing has ever been downloaded — the app was built as of that date.
  return applied ? new Date(applied.appliedAt) : new Date(BASELINE_VERSION);
}

function daysSince(d: Date): number {
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

export function renderContentUpdateStatus(): void {
  const el = byId('content-update-status');
  if (!el) return;

  const current = getCurrentVersion();
  const showAvailable = latestManifest && latestManifest.version !== dismissedVersion;
  const days = daysSince(effectiveContentDate());
  const ageLabel = days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
  const ageColor = days >= STALE_URGENT_DAYS ? '#dc2626' : days >= STALE_WARN_DAYS ? '#b45309' : 'var(--text-muted)';

  let html = `<div style="font-size:11px;color:${ageColor};margin-bottom:8px;">Legal content version: <strong>${current}</strong> (updated ${ageLabel})${checking ? ' — checking for updates…' : ''}</div>`;

  // Only show the standalone staleness notice when there's no update banner
  // already competing for attention below — that banner is the more useful
  // and specific prompt when one's available.
  if (!showAvailable && days >= STALE_WARN_DAYS) {
    const urgent = days >= STALE_URGENT_DAYS;
    html +=
      `<div style="background:${urgent ? '#fef2f2' : '#fffbeb'};border:1px solid ${urgent ? '#fecaca' : '#fde68a'};border-radius:8px;padding:9px 11px;margin-bottom:8px;font-size:11px;color:${urgent ? '#991b1b' : '#78350f'};line-height:1.6;">` +
      `${urgent ? '⚠ ' : ''}It's been ${days} days since your legal content was last updated${urgent ? ' — check before your next trial' : ' — worth checking soon'}.` +
      '</div>';
  }

  if (showAvailable && latestManifest) {
    const m = latestManifest;
    html +=
      '<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 12px;margin-bottom:8px;">';
    html += `<div style="font-size:12px;font-weight:700;color:#1d4ed8;margin-bottom:4px;">Update available: ${m.version} (published ${fmtDate(m.publishedAt)})</div>`;
    html += `<div style="font-size:11px;color:#1e3a5f;margin-bottom:6px;line-height:1.6;">${m.summary}</div>`;
    html += `<div style="font-size:10px;color:#64748b;margin-bottom:6px;">${m.caselawCount} statutes/rules/cases${m.juryResearchCount ? ` · ${m.juryResearchCount} research findings` : ''}</div>`;
    if (m.changelog?.length) {
      html += '<ul style="margin:0 0 8px;padding-left:16px;font-size:10px;color:#334155;line-height:1.6;">';
      m.changelog.forEach((c) => (html += `<li>${c}</li>`));
      html += '</ul>';
    }
    html += '<div style="display:flex;gap:7px;">';
    html += `<button class="btn btn-primary btn-sm" style="flex:1;" ${downloading ? 'disabled' : ''} onclick="downloadContentUpdate()">${downloading ? 'Downloading…' : '⬇ Download Update'}</button>`;
    html += `<button class="btn btn-secondary btn-sm" style="flex:1;" ${downloading ? 'disabled' : ''} onclick="declineContentUpdate()">Not Now</button>`;
    html += '</div></div>';
  }

  html += `<button class="btn btn-secondary btn-sm" style="width:100%;" ${checking ? 'disabled' : ''} onclick="checkForContentUpdate(true)">${checking ? 'Checking…' : '🔄 Check for Legal Content Update'}</button>`;

  el.innerHTML = html;
}

/** Check once on load if already online, and again whenever connectivity returns. */
export function installContentUpdateCheck(): void {
  if (navigator.onLine) void checkForContentUpdate();
  window.addEventListener('online', () => void checkForContentUpdate());
}
